import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Space,
  Table,
} from 'antd';
import { FileExcelFilled } from '@ant-design/icons';
import moment from 'moment';
import { Excel } from 'antd-table-saveas-excel';
import { Link } from 'react-router-dom';
import { SkuService } from '@gtpl/shared-services/masters';
import { RmCountPriceListService } from '@gtpl/shared-services/raw-material-procurement';
import { AlertMessages } from '@gtpl/shared-utils/alert-messages';
import { SkuPriceHistoryDto } from '@gtpl/shared-models/raw-material-procurement';

const { RangePicker } = DatePicker;

interface SkuOption {
  skuCodeId: number;
  shortCode?: string;
  variantCode?: string;
}

export function SkuPriceHistoryView() {
  const [form] = Form.useForm();
  const skuService = new SkuService();
  const priceService = new RmCountPriceListService();
  const [skuOptions, setSkuOptions] = useState<SkuOption[]>([]);
  const [data, setData] = useState<SkuPriceHistoryDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSkus();
    fetchHistory({});
  }, []);

  const loadSkus = async () => {
    try {
      const res = await skuService.getActiveSkuCodes();
      if (res.status) {
        setSkuOptions(res.data || []);
      }
    } catch {
      AlertMessages.getErrorMessage('Failed to load SKU list');
    }
  };

  const getSkuLabel = (sku: SkuOption) => sku.shortCode || sku.variantCode || String(sku.skuCodeId);

  const fetchHistory = async (values?: any) => {
    setLoading(true);
    try {
      const formValues = values || form.getFieldsValue();
      const payload: any = {};
      if (formValues.skuId) payload.skuId = formValues.skuId;
      if (formValues.dateRange?.[0]) payload.fromDate = formValues.dateRange[0].format('YYYY-MM-DD');
      if (formValues.dateRange?.[1]) payload.toDate = formValues.dateRange[1].format('YYYY-MM-DD');

      const res = await priceService.getSkuPriceHistory(payload);
      if (res.status) {
        setData(res.data || []);
      } else {
        setData([]);
        AlertMessages.getErrorMessage(res.internalMessage || 'No history found');
      }
    } catch (err: any) {
      AlertMessages.getErrorMessage(err?.message || 'Failed to load history');
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = () => {
    const excel = new Excel();
    excel
      .addSheet('SKU Price History')
      .addColumns([
        { title: 'Product Sku Name', dataIndex: 'skuName' },
        { title: 'Price', dataIndex: 'price' },
        {
          title: 'Effective From',
          dataIndex: 'effectiveFrom',
          render: (val: string) => moment(val).format('DD-MM-YYYY'),
        },
        {
          title: 'Effective To',
          dataIndex: 'effectiveTo',
          render: (_val: string, record: SkuPriceHistoryDto) =>
            record.isCurrent || !record.effectiveTo
              ? 'Present'
              : moment(record.effectiveTo).format('DD-MM-YYYY'),
        },
      ])
      .addDataSource(
        data.map((row) => ({
          ...row,
          effectiveFrom: moment(row.effectiveFrom).format('DD-MM-YYYY'),
          effectiveTo:
            row.isCurrent || !row.effectiveTo
              ? 'Present'
              : moment(row.effectiveTo).format('DD-MM-YYYY'),
        })),
      )
      .saveAs('sku-price-history.xlsx');
  };

  const columns = [
    {
      title: 'S.No',
      key: 'sNo',
      width: 70,
      render: (_: any, __: any, index: number) => index + 1,
    },
    { title: 'Product Sku Name', dataIndex: 'skuName', key: 'skuName' },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      render: (val: number) => Number(val).toFixed(2),
    },
    {
      title: 'Effective From',
      dataIndex: 'effectiveFrom',
      key: 'effectiveFrom',
      render: (val: string) => moment(val).format('DD-MM-YYYY'),
    },
    {
      title: 'Effective To',
      key: 'effectiveTo',
      render: (_: any, record: SkuPriceHistoryDto) =>
        record.isCurrent || !record.effectiveTo
          ? 'Present'
          : moment(record.effectiveTo).format('DD-MM-YYYY'),
    },
  ];

  return (
    <Card
      title={<span style={{ color: 'white' }}> Product Sku Rates Logs</span>}
      style={{ margin: 20 }}
      headStyle={{
        backgroundColor: '#69c0ff',
        border: 0,
        textAlign: 'center',
      }}
      extra={
        <Space>
          <Link to="/sku-price-form">
            <Button type="primary">Add Price</Button>
          </Link>
          <Link to="/sku-price-view">
            <Button>Current Prices</Button>
          </Link>
          {/* <Button icon={<FileExcelFilled />} onClick={exportExcel} disabled={!data.length}>
            Export Excel
          </Button> */}
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={fetchHistory}>
        <Row gutter={12}>
          <Col xs={12} md={6}>
            <Form.Item name="skuId" label="Product Sku">
              <Select showSearch allowClear placeholder="Select Product Sku" optionFilterProp="children">
                {skuOptions.map((sku) => (
                  <Select.Option key={sku.skuCodeId} value={sku.skuCodeId}>
                    {getSkuLabel(sku)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          {/* <Col xs={24} md={10}>
            <Form.Item name="dateRange" label="Date Range">
              <RangePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
            </Form.Item>
          </Col> */}
          <Col xs={24} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  Search
                </Button>
                <Button
                  onClick={() => {
                    form.resetFields();
                    setData([]);
                  }}
                >
                  Reset
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Table
        columns={columns}
        dataSource={data}
        rowKey={(record, index) => `${record.skuId}-${record.effectiveFrom}-${index}`}
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        scroll={{ x: true }}
      />
    </Card>
  );
}

export default SkuPriceHistoryView;
